'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useParams } from 'next/navigation';

import type { CommunityMember } from '@zealy/queries';
import type { BlockchainNetwork } from '@zealy/utils';
import { TaskCard, TaskCardHint } from '@zealy/design-system';
import { QuestionFilled } from '@zealy/icons';
import { useAuthenticatedUser, useCommunity } from '@zealy/queries';
import { roleIsAtLeast, truncateAddress } from '@zealy/utils';

import { TASK_ICONS } from '#constants/quests/TaskIcons';

// Dynamically import the ConnectWallet component to prevent server-side rendering
const ConnectButton = dynamic(
  () => import('#features/Wallets/ConnectWallet').then(mod => ({ default: mod.ConnectWallet })),
  { ssr: false },
);

const ConnectWallet = ({ blockchain = 'eth-mainnet' }: { blockchain: BlockchainNetwork }) => {
  const { subdomain } = useParams<{ subdomain: string }>();
  const { data: community } = useCommunity(subdomain);
  const user = useAuthenticatedUser<CommunityMember>();
  const userRelatedAddress = user?.data?.addresses?.[blockchain];
  const tChain = useTranslations('quest.type.nft.fields.network.options');
  const t = useTranslations('settings.linked-accounts');
  const title = `${t(userRelatedAddress ? 'update' : 'connect')} ${tChain(blockchain)} ${t(
    'wallet.title',
  )}`;

  const isAdmin = roleIsAtLeast(user?.data?.role, 'admin');
  const shouldShowHint = isAdmin && community?.requiredFields?.linkWallet;

  return (
    <TaskCard
      title={title}
      type="wallet"
      className="px-200 py-300"
      icon={TASK_ICONS.wallet}
      hint={
        shouldShowHint && (
          <TaskCardHint
            icon={<QuestionFilled />}
            tooltip={t.rich('wallet.hint', {
              link: chunks => (
                <Link href={`/cw/${subdomain}/settings/security#access`} className="underline">
                  {chunks}
                </Link>
              ),
            })}
          >
            {t('wallet.hint-trigger')}
          </TaskCardHint>
        )
      }
    >
      <ConnectButton
        blockchain={blockchain}
        buttonProps={{
          color: 'default',
          className: 'w-full muted button-lg',
          size: 'lg',
          ...(userRelatedAddress && {
            children: `${t('update')}: ${truncateAddress(userRelatedAddress)}`,
          }),
        }}
      />
    </TaskCard>
  );
};

export default ConnectWallet;
